import { useEffect, useState } from 'react';
import { api, can, getUser } from '../../../lib/api';
import { Card, Field, useToast } from '../../../components/ui';
import type { PointRow } from '../types';

/**
 * When the banner asks again. Blank: when the notice version changes. A number:
 * that many days after the choice, and on a notice change regardless. Stored on
 * the collection point (consentMaxAgeDays), the same field the consent widget
 * reads, so it saves on its own.
 */
export default function BehaviourCard({ banner, onChanged }: { banner: PointRow; onChanged: () => void }) {
  const toast = useToast();
  const editable = can(getUser(), 'cookies:edit');
  const stored = banner.consentMaxAgeDays ?? null;
  const [days, setDays] = useState<string>(stored ? String(stored) : '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setDays(stored ? String(stored) : ''); }, [banner.id, stored]);

  const n = days.trim() === '' ? null : Number(days);
  const valid = n === null || (Number.isInteger(n) && n >= 1 && n <= 3650);
  const dirty = n !== stored;

  async function save() {
    setBusy(true);
    try {
      await api.put(`/v1/collection-points/${banner.id}/cookie-settings`, { consentMaxAgeDays: n });
      toast('Saved.', 'ok');
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Behaviour">
      <div className="row">
        <Field label="Ask again after" help="Days. Blank: only when the notice changes. The banner always asks again when the notice version changes.">
          <input
            type="number" min={1} max={3650} step={1} value={days} placeholder="When the notice changes" readOnly={!editable}
            className={valid ? '' : 'invalid'}
            onChange={(e) => setDays(e.target.value)}
          />
        </Field>
        {editable && (
          <div className="tight">
            <div className="field">
              <button className="primary" onClick={save} disabled={busy || !dirty || !valid}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}
      </div>
      {!valid && <div className="note bad">Between 1 and 3650 days.</div>}
    </Card>
  );
}
